import { ApexOptions } from 'apexcharts'
import { fNumber } from 'common/utils/formatNumber'
import React from 'react'
import ReactApexChart from 'react-apexcharts'

import { Box, CardContent, Chip, Typography, Skeleton } from '@mui/material'
import Stack from '@mui/system/Stack'

type Item = { label: string; value: number; color: string; percent: number }

type Props = {
  items: Item[]
  totalValue: number
  donutOptions: ApexOptions
  donutSeries: number[]
  t: (key: string, def?: string) => string
  loading?: boolean
}

const DonutChart: React.FC<Props> = ({
  items,
  totalValue,
  donutOptions,
  donutSeries,
  t,
  loading,
}) => {
  if (loading) {
    return (
      <CardContent>
        <Stack direction={"row"} justifyContent={'center'}>
          <Skeleton variant="circular" width={200} height={200} />
        </Stack>

        <Box sx={{ mt: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                py: 1,
              }}
            >
              <Skeleton variant="circular" width={10} height={10} />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="30%" />
            </Box>
          ))}
        </Box>
      </CardContent>
    )
  }

  return (
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ position: 'relative', mb: 2 }}>
        <ReactApexChart options={donutOptions} series={donutSeries} type="donut" height={200} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('summary.cost.total.label')}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            ฿{fNumber(totalValue)}
          </Typography>
        </Box>
      </Box>

      {items.map((it, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            py: 1,
            px: 0.5,
            borderRadius: 1,
            backgroundColor: 'transparent',
          }}
        >
          <Chip sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: it.color }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {it.label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2">฿{fNumber(it.value)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {fNumber(it.percent)}%
            </Typography>
          </Box>
        </Box>
      ))}
    </CardContent>
  )
}

export default DonutChart
